"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useEffect } from "react";
import { _ } from "@/lib/api/i18n";
import { useMenu } from "@/lib/context/menu";
import type { MenuItem } from "@/lib/api/types";

const findFirstChildUrl = (item: MenuItem): string | undefined => {
    if (item.to) return item.to;
    if (item.items) {
        for (const child of item.items) {
            const url = findFirstChildUrl(child);
            if (url) return url;
        }
    }
    return undefined;
};

const hasActiveChild = (item: MenuItem, asPath: string): boolean => {
    if (item.to && item.to === asPath) return true;
    if (item.items) {
        return item.items.some((child) => hasActiveChild(child, asPath));
    }
    return false;
};

interface AppMenuitemProps {
    item: MenuItem;
    root: boolean;
    index: number;
    label: string;
    parentKey?: string;
}

export const AppMenuitem = (props: AppMenuitemProps) => {
    const { setActiveMenu } = useMenu();
    const pathname = usePathname();
    const item = props.item;
    const key = ((props.parentKey || "") + (item.to?.toLowerCase().replaceAll("/", "Bar") || "")) || item.label || "";

    const childUrl = props.root && item.items ? findFirstChildUrl(item) : null;
    const effectiveTo = item.to || childUrl || undefined;
    const isActiveRoute = effectiveTo && pathname === effectiveTo;
    const isActiveParent = props.root && item.items && hasActiveChild(item, pathname);
    const active = isActiveRoute || isActiveParent;

    useEffect(() => {
        if (effectiveTo && pathname === effectiveTo) {
            setActiveMenu(key);
        }
    }, [pathname]);

    const itemClick = (event: React.MouseEvent) => {
        if (item.disabled) {
            event.preventDefault();
            return;
        }
        if (item.command) {
            item.command({ originalEvent: event, item });
        }
        setActiveMenu(key);
    };

    return (
        <li
            className={`layout-root-menuitem ${props.root ? "layout-root-menuitem" : ""} ${active ? "active-menuitem active-route" : ""}`}
        >
            {effectiveTo && item.visible !== false ? (
                <Link
                    href={effectiveTo}
                    replace={item.replaceUrl}
                    target={item.target}
                    onClick={(e) => itemClick(e)}
                    className={`flex items-center text-sm font-semibold uppercase whitespace-nowrap overflow-hidden rounded-lg border border-transparent px-4 py-3 cursor-pointer transition-colors ${
                        active
                            ? "text-[var(--primary-color)] bg-card border-[var(--primary-color)]"
                            : "text-foreground hover:bg-accent hover:border-border"
                    }`}
                    tabIndex={0}
                >
                    {item.icon && <span className="layout-menuitem-icon mr-2 text-sm">{item.icon}</span>}
                    <span
                        title={_(item.help || "")}
                        className="layout-menuitem-text opacity-0 transition-opacity"
                    >
                        {_(item.label || "")}
                    </span>
                </Link>
            ) : null}
        </li>
    );
};
